import { useState } from 'react'
import { forgetRoom, gotoRoom, newRoomId, parseRoomId, recentRooms } from './room'
import { LangToggle, useI18n, type I18n } from './i18n'

/**
 * トップ画面。ルームを作るか、ルームIDで参加するか。
 *
 * **ルーム作成はサーバに何も問い合わせない。** クライアントが ID を生成して
 * `/r/<id>` へ遷移し、最初の WebSocket 接続でルームが生まれる
 * → doc/sharing/master.md#画面の流れ
 */
export function Home({ badRoomId = false }: { badRoomId?: boolean }) {
  const { t } = useI18n()
  const [input, setInput] = useState('')
  // 文言ではなく「不正だったか」だけを持つ。言語を変えたらその場で訳し直るように
  const [badId, setBadId] = useState(badRoomId)
  const [recent, setRecent] = useState(recentRooms)

  const join = () => {
    const id = parseRoomId(input)
    if (!id) {
      setBadId(true)
      return
    }
    gotoRoom(id)
  }

  return (
    <div className="home">
      <div className="home__card">
        <h1 className="home__title">Tarkov Map Pins</h1>
        <p className="home__lead">
          {t('home.lead')}
          <br />
          {t('home.lead2')}
        </p>

        <button type="button" className="home__create" onClick={() => gotoRoom(newRoomId())}>
          {t('home.create')}
        </button>

        <div className="home__join">
          <label htmlFor="roomid">{t('home.join')}</label>
          <div className="home__joinrow">
            <input
              id="roomid"
              className="control"
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                setBadId(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') join()
              }}
              placeholder="xY3k_7Qm2pLbN8vR4sT1wA"
              spellCheck={false}
            />
            <button type="button" className="control" onClick={join}>
              {t('home.joinBtn')}
            </button>
          </div>
          {badId && <p className="home__error">{t('home.badId')}</p>}
        </div>

        {recent.length > 0 && (
          <div className="home__recent">
            <span className="home__recentlabel">{t('home.recent')}</span>
            <ul>
              {recent.map((r) => (
                <li key={r.id}>
                  <button type="button" className="home__recentitem" onClick={() => gotoRoom(r.id)}>
                    <code>{r.id}</code>
                    <span className="home__ago">{ago(r.at, t)}</span>
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t('home.forget')}
                    title={t('home.forget')}
                    onClick={() => {
                      forgetRoom(r.id)
                      setRecent(recentRooms())
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <p className="home__note">{t('home.note')}</p>
          </div>
        )}

        <p className="home__warn">
          {t('home.warn')}
          <br />
          {t('home.warn2')}
        </p>

        <p className="home__credit">
          {t('home.credit')} <a href="/about">{t('about.title')}</a>
        </p>

        <LangToggle className="home__lang" />
      </div>
    </div>
  )
}

function ago(at: number, t: I18n['t']): string {
  const min = Math.floor((Date.now() - at) / 60_000)
  if (min < 1) return t('home.ago.now')
  if (min < 60) return t('home.ago.min', { n: min })
  const hour = Math.floor(min / 60)
  if (hour < 24) return t('home.ago.hour', { n: hour })
  return t('home.ago.day', { n: Math.floor(hour / 24) })
}
